// @flow
import get from 'lodash/get';
import { createAction } from 'redux-act';

import type { Currency } from '@gandi/billing';
import type { BundleType } from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';

import { createLogger } from 'gandi.redux-logger';

import type { GetState, Dispatch } from '../../../types';
import type {
  Bundle,
  AvailableBundleApi,
  BundlePricingApi,
  TaxedPrice,
} from '../../types';
import { formatSuggestionPrices, addTaxedPrices } from '../helpers/pricer';
import { selectBundle } from '../selectors/bundle';

import {
  selectRawList as selectResults,
  selectOne as selectOneResult,
} from '../selectors/results';

const logger = createLogger('DomainCreate/modules/actions/bundle');

export const set: {
  (...Array<AvailableBundleApi>): {
    meta?: mixed,
    payload: mixed,
    type: string,
    ...
  },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[bundle] Set');
export const setPricing: {
  (
    ...Array<{
      type: BundleType,
      items: Array<{ code: string, fqdn: Fqdn }>,
      total: {
        currency: Currency,
        newComerPrice: TaxedPrice,
        normalPrice: TaxedPrice,
        price: TaxedPrice,
        ...
      },
    }>
  ): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[bundle] Set pricing');
export const reset: {
  (...Array<any>): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
} = createAction('[bundle] Reset');

export const receiveBundle =
  (
    bundle: AvailableBundleApi,
  ): ((dispatch: Dispatch, getState: GetState) => void) =>
  (dispatch: Dispatch, getState: GetState) => {
    // event: bundle_available
    // {
    //   "type": "popularity",
    //   "content": ["parisweb.tools", "parisweb.app", "parisweb.design"],
    //   "grids": ["A", "B", "C"]
    // }
    const results = selectResults(getState());
    const matchedResults = results.filter(
      result => bundle.content.indexOf(result.fqdn) > -1,
    );
    const valid =
      bundle.content.length > 1 &&
      matchedResults.length === bundle.content.length;

    if (!valid) {
      const err = new Error(
        'Cleaned-up invalid bundle state: bundle_available',
      );
      dispatch(logger.error({ err }, err.message));
      return;
    }

    dispatch(set(bundle));
  };

export const receiveBundlePricing =
  (
    pricing: BundlePricingApi,
  ): ((dispatch: Dispatch, getState: GetState) => void) =>
  (dispatch: Dispatch, getState: GetState) => {
    const bundle: ?Bundle = selectBundle(getState(), pricing.type);

    if (!bundle) {
      dispatch(reset(getState(), pricing.type));
      return;
    }

    const matchedBundleItems = bundle.content.filter(
      fqdn => pricing.items.map(item => item.fqdn).indexOf(fqdn) > -1,
    );

    const validBundle = !!pricing.success;
    // re-check `!!state.bundle` to make Flow happy.
    const validBundleType = !!bundle && bundle.type === pricing.type;
    const validBundleItems =
      matchedBundleItems.length > 0 &&
      matchedBundleItems.length === pricing.items.length;

    const validSuggestionsPricing =
      !!bundle &&
      !!bundle.content &&
      bundle.content.every(fqdn => {
        const result = selectOneResult(getState(), fqdn);

        return (
          !!result &&
          !!result.prices &&
          !!result.prices.create &&
          !!result.prices.create.golive &&
          !!result.prices.create.golive.price
        );
      });

    const valid = validBundle && validBundleType && validBundleItems;

    if (!valid) {
      const err = new Error('Cleaned-up invalid bundle state: bundle_pricing');
      dispatch(logger.error({ err }, err.message));

      dispatch(reset(getState, pricing.type));
      return;
    }

    // Best-effort handling of a race-condition in aiosuggest which sends back
    // the bundle pricing event before the individual suggestions pricing event.
    if (!validSuggestionsPricing) {
      const err = new Error('Best-effort invalid bundle state: bundle_pricing');
      dispatch(logger.error({ err }, err.message));
    }

    // Calculate normal price, bundle price and welcome offer price
    const { ...total } = pricing.items.reduce(
      (
        acc: {
          currency: Currency,
          newComerPrice: TaxedPrice,
          normalPrice: TaxedPrice,
          price: TaxedPrice,
          ...
        },
        item,
      ) => {
        const result = selectOneResult(getState(), item.fqdn);
        const formattedPrices = formatSuggestionPrices({
          phase: 'golive',
          prices: item.prices,
        });

        const normalPrice: TaxedPrice = validSuggestionsPricing
          ? // $FlowFixMe `result` is checked before.
            result.prices.create.golive.discount?.normalPrice ||
            // $FlowFixMe `result` is checked before.
            result.prices.create.golive.price
          : // $FlowFixMe `formattedPrices` is checked before.
            formattedPrices.prices.create.golive.price;
        const price: TaxedPrice =
          // $FlowFixMe `formattedPrices` is checked before.
          formattedPrices.prices.create.golive.discount?.price ||
          // $FlowFixMe `formattedPrices` is checked before.
          formattedPrices.prices.create.golive.price;

        const newComerPrice: TaxedPrice = price;

        return {
          ...acc,
          newComerPrice: addTaxedPrices(acc.newComerPrice, newComerPrice),
          normalPrice: addTaxedPrices(acc.normalPrice, normalPrice),
          price: addTaxedPrices(acc.price, price),
        };
      },
      {
        currency: get(pricing, 'items.0.prices.currency'),
        newComerPrice: { afterTaxes: 0, beforeTaxes: 0 },
        normalPrice: { afterTaxes: 0, beforeTaxes: 0 },
        price: { afterTaxes: 0, beforeTaxes: 0 },
      },
    );

    const validBundleTotal = Object.keys(total)
      .map(key => total[key])
      .every(Boolean);

    if (!validBundleTotal) {
      if (!validSuggestionsPricing) {
        const err = new Error('Cleaned-up invalid bundle state: bundle total');
        dispatch(logger.error({ err }, err.message));
      }
      dispatch(reset(getState(), pricing.type));
    }

    const items = pricing.items.map(({ fqdn, code }) => ({ fqdn, code }));
    dispatch(setPricing({ type: pricing.type, items, total }));
  };
